import React from 'react';
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import FEditableGrid, { EditablePageField, FEditableGridColumn, FEditableGridPayload, FEditableGridRow } from '../../../components/egrid';

import InvestmentBudgetCardsPanel from './InvestmentBudgetCardsPanel';
import InvestmentBudgetRollupPanel from './InvestmentBudgetRollupPanel';
import { InvestmentBudgetCard, InvestmentBudgetRollupRow } from './types';
import { fetchInvestmentBudgetCards, fetchInvestmentBudgetRollup, fetchInvestmentBudgetRows, saveInvestmentBudgetRows } from './investmentBudgetApi';
import { createFieldObject, type EFieldConfig } from '../../../components/form';
import { formatDate } from '../../../components/renderers';

type State = {
  cycleCode: string;
  periodCode: string;
  costCenterCode: string;
  typeCode: string;
  priorityCode: string;
  loading: boolean;
  error: string;
  cards: InvestmentBudgetCard | null;
  rows: FEditableGridRow[];
  rollup: InvestmentBudgetRollupRow[];
};

const gform: FEditableGridColumn[] = [
  { id: 'shc', label: 'Code', type: 'tf', req: 1, width: 150 },
  { id: 'nam', label: 'Investment Name', type: 'tf', req: 1, width: 240 },
  // { id: 'ivt', label: 'Type', type: 'fa', req: 1, width: 140, defaultValue: 'CAPEX', ops: { items: [
  //   { v: 'CAPEX', t: 'CAPEX' },
  //   { v: 'EQUIPMENT', t: 'Equipment' },
  //   { v: 'INFRASTRUCTURE', t: 'Infrastructure' },
  //   { v: 'IT', t: 'IT' },
  //   { v: 'VEHICLE', t: 'Vehicle' },
  // ] } },
  // { id: 'prc', label: 'Priority', type: 'fa', req: 1, width: 140, defaultValue: 'MEDIUM', ops: { items: [
  //   { v: 'HIGH', t: 'High' },
  //   { v: 'MEDIUM', t: 'Medium' },
  //   { v: 'LOW', t: 'Low' },
  // ] } },
  { id: 'ivt', label: 'Type', type: 'fa', req: 1, width: 140, defaultValue: 'CAPEX',cp:{t:'lcs',s:'investmenttype'}},
  { id: 'prc', label: 'Priority', type: 'fa', req: 1, width: 140, defaultValue: 'MEDIUM', cp:{t:'lcs',s:'investmentpriority'}},
  { id: 'acc', label: 'Account', type: 'tf', req: 0, width: 140 },
  { id: 'dsc', label: 'Description', type: 'tf', req: 0, width: 260 },
  { id: 'qty', label: 'Qty', type: 'nf', req: 1, width: 100, defaultValue: 1 },
  { id: 'ucs', label: 'Unit Cost', type: 'nf', req: 1, width: 130, defaultValue: 0 },
  { id: 'rqa', label: 'Requested', type: 'nf', req: 1, width: 140, defaultValue: 0 },
  { id: 'apa', label: 'Approved', type: 'nf', req: 0, width: 140, defaultValue: 0 },
  { id: 'psd', label: 'Start Date', type: 'tf', req: 0, width: 130 },
  { id: 'ped', label: 'End Date', type: 'tf', req: 0, width: 130 },
  { id: 'pmd', label: 'Procurement Mode', type: 'tf', req: 0, width: 180 },
  { id: 'jus', label: 'Justification', type: 'tf', req: 1, width: 280 },
  { id: 'bnf', label: 'Expected Benefit', type: 'tf', req: 0, width: 280 },
  { id: 'rsk', label: 'Risk Note', type: 'tf', req: 0, width: 240 },
];

class InvestmentBudgetWorkbench extends React.Component<any, State> {
  state: State = {
    cycleCode: '2025',
    periodCode: '',
    costCenterCode: '',
    typeCode: '',
    priorityCode: '',
    loading: false,
    error: '',
    cards: null,
    rows: [],
    rollup: [],
  };

  componentDidMount() {
    this.load();
  }

  formconfigs: { [name: string]: EFieldConfig } = {
    bcc: { id: 'bcc', label: 'Budget Cycle', type: 'fc', req: 1, width: 120, cp: { t: 'bcs', s: 'budgetcycle' } },
    bpc: { id: 'bpc', label: 'Period', type: 'fc', req: 0, width: 120, cp: { t: 'bcs', s: 'budgetperiod' } },
    ccc: { id: 'ccc', label: 'Cost Center', type: 'fc', req: 1, width: 150, cp: { t: 'bcs', s: 'costcenter' } },
  };

  makefield = (fieldconf: EFieldConfig): any => {
    const { service = 'investmentbudget', record = {} } = this.props;
    const mode = 'add';
    const formatters = { dob: formatDate };
    const onChange = this.onChange.bind(this);
    const extras = {};
    return createFieldObject(service, mode, fieldconf, record, formatters, onChange, extras);
  };

  onChange = (name: 'cycleCode' | 'periodCode' | 'costCenterCode') => (e: any) => {
    const raw = e?.target ? e.target.value : e;
    this.setState({ [name]: String(raw || '').toUpperCase() } as Pick<State, 'cycleCode' | 'periodCode' | 'costCenterCode'>);
  };

  setField = (name: 'cycleCode' | 'periodCode' | 'costCenterCode' | 'typeCode' | 'priorityCode') => (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ [name]: e.target.value.toUpperCase() } as Pick<State, 'cycleCode' | 'periodCode' | 'costCenterCode' | 'typeCode' | 'priorityCode'>);
  };

  filters = () => {
    const { cycleCode, periodCode, costCenterCode, typeCode, priorityCode } = this.state;
    return {
      bcc: cycleCode || null,
      bpc: periodCode || null,
      ccc: costCenterCode || null,
      ivt: typeCode || null,
      prc: priorityCode || null,
    };
  };

  load = async () => {
    this.setState({ loading: true, error: '' });

    try {
      const filters = this.filters();
      const [cards, rows, rollup] = await Promise.all([
        fetchInvestmentBudgetCards(filters),
        fetchInvestmentBudgetRows(filters),
        fetchInvestmentBudgetRollup(filters),
      ]);

      this.setState({ cards, rows: rows as any, rollup });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  submit = async (payload: FEditableGridPayload) => {
    const filters = this.filters();
    if (!filters.ccc) throw new Error('Cost Center is required for investment budget save.');
    await saveInvestmentBudgetRows(filters, payload.rcd);
    await this.load();
  };

  render() {
    const { cycleCode, periodCode, costCenterCode, typeCode, priorityCode, loading, error, cards, rows, rollup } = this.state;
    const { bcc: bccconf, bpc: bpcconf, ccc: cccconf } = this.formconfigs;
    const bccfield = this.makefield(bccconf);
    const bpcfield = this.makefield(bpcconf);
    const cccfield = this.makefield(cccconf);

    return (
      <Box sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Investment Budget Workbench</Typography>
            <Typography variant="body2" color="text.secondary">
              Prepare, justify, prioritize and approve CAPEX / investment budget requests.
            </Typography>
          </Box>
          <Button variant="contained" onClick={this.load} disabled={loading}>Refresh</Button>
        </Stack>

        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <EditablePageField label="Budget Cycle" fieldobject={bccfield} value={cycleCode} onChange={this.onChange('cycleCode')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <EditablePageField label="Period" fieldobject={bpcfield} value={periodCode} onChange={this.onChange('periodCode')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <EditablePageField label="Cost Center" fieldobject={cccfield} value={costCenterCode} onChange={this.onChange('costCenterCode')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <TextField select label="Type" value={typeCode} onChange={this.setField('typeCode')} fullWidth>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CAPEX">CAPEX</MenuItem>
                <MenuItem value="EQUIPMENT">Equipment</MenuItem>
                <MenuItem value="INFRASTRUCTURE">Infrastructure</MenuItem>
                <MenuItem value="IT">IT</MenuItem>
                <MenuItem value="VEHICLE">Vehicle</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <TextField select label="Priority" value={priorityCode} onChange={this.setField('priorityCode')} fullWidth>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}><CircularProgress size={22} /><Typography>Loading investment budget...</Typography></Stack>}

        <Stack spacing={3}>
          <InvestmentBudgetCardsPanel cards={cards} />

          <Paper elevation={1} sx={{ p: 2 }}>
            <FEditableGrid
              title="Investment Budget Lines"
              gform={gform}
              value={rows}
              onChange={(rows) => this.setState({ rows })}
              onSubmit={this.submit}
              height={560}
            />
          </Paper>

          <InvestmentBudgetRollupPanel rows={rollup} />
        </Stack>
      </Box>
    );
  }
}

export default InvestmentBudgetWorkbench;
